'use client';

import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface AccountData {
  totalValue: number;
  change24h: number;
  changePercent24h: number;
  chartData: Array<{ time: number; deepseek: number; doubao: number; qwen: number; macd: number }>;
}

type TimeRange = 'ALL' | '72H' | '24H' | '12H' | '5H';

// 自定义 Tooltip 组件
function CustomTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = new Date(data.time);

    return (
      <div className="rounded border border-white/20 bg-black px-3 py-2 font-mono text-[10px]">
        <p className="text-gray-500 mb-1.5">
          {date.toLocaleString('zh-CN', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="font-semibold" style={{ color: entry.color }}>
            {entry.name}: ${entry.value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export function AccountValue() {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState<TimeRange>('5H');

  // Default initial balance from environment variable (used as fallback)
  const DEFAULT_INITIAL_BALANCE = Number(process.env.NEXT_PUBLIC_INITIAL_BALANCE) || 1000;

  // Store initial balances for each bot (from API statistics)
  const [botInitialBalances, setBotInitialBalances] = useState({
    deepseek: DEFAULT_INITIAL_BALANCE,
    doubao: DEFAULT_INITIAL_BALANCE,
    qwen: DEFAULT_INITIAL_BALANCE,
    macd: DEFAULT_INITIAL_BALANCE
  });

  const [accountData, setAccountData] = useState<AccountData>({
    totalValue: DEFAULT_INITIAL_BALANCE * 4, // 4 bots
    change24h: 0,
    changePercent24h: 0,
    chartData: []
  });

  // 从机器人 API 获取真实账户数据
  useEffect(() => {
    const fetchBotStatus = async () => {
      try {
        const response = await fetch('/api/bot');
        const data = await response.json();

        if (data.deepseek || data.doubao || data.qwen || data.macd) {
          // 获取每个机器人的 initialBalance（从统计数据中）
          const deepseekInitialBalance = data.deepseek?.statistics?.initialBalance || DEFAULT_INITIAL_BALANCE;
          const doubaoInitialBalance = data.doubao?.statistics?.initialBalance || DEFAULT_INITIAL_BALANCE;
          const qwenInitialBalance = data.qwen?.statistics?.initialBalance || DEFAULT_INITIAL_BALANCE;
          const macdInitialBalance = data.macd?.statistics?.initialBalance || DEFAULT_INITIAL_BALANCE;

          // 获取 DeepSeek 数据
          const deepseekEquity = data.deepseek?.status?.accountState?.totalBalance +
            (data.deepseek?.status?.accountState?.unrealizedPnl || 0) || deepseekInitialBalance;
          const deepseekHistory = data.deepseek?.status?.accountState?.accountValueHistory || [];

          // 获取豆包数据
          const doubaoEquity = data.doubao?.status?.accountState?.totalBalance +
            (data.doubao?.status?.accountState?.unrealizedPnl || 0) || doubaoInitialBalance;
          const doubaoHistory = data.doubao?.status?.accountState?.accountValueHistory || [];

          // 获取通义千问数据
          const qwenEquity = data.qwen?.status?.accountState?.totalBalance +
            (data.qwen?.status?.accountState?.unrealizedPnl || 0) || qwenInitialBalance;
          const qwenHistory = data.qwen?.status?.accountState?.accountValueHistory || [];

          // 获取 MACD 策略数据
          const macdEquity = data.macd?.status?.accountState?.totalBalance +
            (data.macd?.status?.accountState?.unrealizedPnl || 0) || macdInitialBalance;
          const macdHistory = data.macd?.status?.accountState?.accountValueHistory || [];

          // 合并历史数据
          const timeMap = new Map<number, { deepseek?: number; doubao?: number; qwen?: number; macd?: number }>();

          deepseekHistory.forEach((point: any) => {
            timeMap.set(point.timestamp, { deepseek: point.value });
          });

          doubaoHistory.forEach((point: any) => {
            const existing = timeMap.get(point.timestamp);
            if (existing) {
              existing.doubao = point.value;
            } else {
              timeMap.set(point.timestamp, { doubao: point.value });
            }
          });

          qwenHistory.forEach((point: any) => {
            const existing = timeMap.get(point.timestamp);
            if (existing) {
              existing.qwen = point.value;
            } else {
              timeMap.set(point.timestamp, { qwen: point.value });
            }
          });

          macdHistory.forEach((point: any) => {
            const existing = timeMap.get(point.timestamp);
            if (existing) {
              existing.macd = point.value;
            } else {
              timeMap.set(point.timestamp, { macd: point.value });
            }
          });

          // 转换为图表数据
          const sortedTimes = Array.from(timeMap.keys()).sort();
          let lastDeepseek = deepseekInitialBalance;
          let lastDoubao = doubaoInitialBalance;
          let lastQwen = qwenInitialBalance;
          let lastMacd = macdInitialBalance;

          const chartData = sortedTimes.map(time => {
            const point = timeMap.get(time)!;
            if (point.deepseek !== undefined) lastDeepseek = point.deepseek;
            if (point.doubao !== undefined) lastDoubao = point.doubao;
            if (point.qwen !== undefined) lastQwen = point.qwen;
            if (point.macd !== undefined) lastMacd = point.macd;

            return {
              time,
              deepseek: point.deepseek ?? lastDeepseek,
              doubao: point.doubao ?? lastDoubao,
              qwen: point.qwen ?? lastQwen,
              macd: point.macd ?? lastMacd
            };
          });

          const finalChartData = chartData.length > 0 ? chartData : [{
            time: Date.now(),
            deepseek: deepseekEquity,
            doubao: doubaoEquity,
            qwen: qwenEquity,
            macd: macdEquity
          }];

          const totalEquity = deepseekEquity + doubaoEquity + qwenEquity + macdEquity;
          const totalInitialBalance = deepseekInitialBalance + doubaoInitialBalance + qwenInitialBalance + macdInitialBalance;
          const change24h = totalEquity - totalInitialBalance;
          const changePercent24h = (change24h / totalInitialBalance) * 100;

          // Update bot initial balances state
          setBotInitialBalances({
            deepseek: deepseekInitialBalance,
            doubao: doubaoInitialBalance,
            qwen: qwenInitialBalance,
            macd: macdInitialBalance
          });

          setAccountData({
            totalValue: totalEquity,
            change24h,
            changePercent24h,
            chartData: finalChartData
          });
        }
      } catch (error) {
        console.error('Failed to fetch bot status:', error);
      }
    };

    fetchBotStatus();
    const interval = setInterval(fetchBotStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  const isPositive = accountData.changePercent24h >= 0;

  const formatXAxis = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatYAxis = (value: number) => {
    return `$${value.toFixed(0)}`;
  };

  // 根据时间范围过滤数据
  const getFilteredData = () => {
    const now = Date.now();
    const ranges: Record<TimeRange, number> = {
      'ALL': 0,
      '72H': 72 * 60 * 60 * 1000,
      '24H': 24 * 60 * 60 * 1000,
      '12H': 12 * 60 * 60 * 1000,
      '5H': 5 * 60 * 60 * 1000
    };

    const rangeMs = ranges[timeRange];
    if (rangeMs === 0) return accountData.chartData;

    return accountData.chartData.filter(point => now - point.time <= rangeMs);
  };

  const filteredData = getFilteredData();

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="flex items-end justify-between border-b border-white/10 pb-4">
        <div>
          <h2 className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {t.totalAccountValue}
          </h2>
          <div className="font-mono text-5xl font-bold tracking-tight text-white">
            ${accountData.totalValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </div>
          <div className={`mt-2 font-mono text-sm ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}${accountData.change24h.toFixed(2)} (
            {isPositive ? '+' : ''}{accountData.changePercent24h.toFixed(2)}%)
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex gap-2">
          {(['5H', '12H', '24H', '72H', 'ALL'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`rounded border px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                timeRange === range
                  ? 'border-white bg-white text-black'
                  : 'border-white/20 text-gray-500 hover:border-white/40 hover:text-gray-300'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Section */}
      <div className="rounded border border-white/10 bg-zinc-950/50 p-6">
        {filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={filteredData}
              margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="time"
                tickFormatter={formatXAxis}
                stroke="rgba(255,255,255,0.2)"
                style={{ fontSize: '10px', fontFamily: 'monospace' }}
                tick={{ fill: 'rgba(255,255,255,0.4)' }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={formatYAxis}
                stroke="rgba(255,255,255,0.2)"
                style={{ fontSize: '10px', fontFamily: 'monospace' }}
                tick={{ fill: 'rgba(255,255,255,0.4)' }}
                tickLine={false}
                width={60}
                domain={['auto', 'auto']}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* DeepSeek Line */}
              <Line
                type="monotone"
                dataKey="deepseek"
                name="DeepSeek-V3"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
              />
              {/* Doubao Line */}
              <Line
                type="monotone"
                dataKey="doubao"
                name="Doubao"
                stroke="#a855f7"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
              />
              {/* Qwen Line */}
              <Line
                type="monotone"
                dataKey="qwen"
                name="Qwen"
                stroke="#f97316"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
              />
              {/* MACD Strategy Line */}
              <Line
                type="monotone"
                dataKey="macd"
                name="MACD Strategy"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center font-mono">
              <div className="mb-2 text-2xl text-gray-600">━</div>
              <p className="text-xs text-gray-600 uppercase tracking-wider">{t.accountValue.waitingForData}</p>
            </div>
          </div>
        )}
      </div>

      {/* Model Performance Cards */}
      <div className="grid grid-cols-4 gap-4">
        {/* DeepSeek Card */}
        <div className="rounded border border-blue-500/20 bg-zinc-950/50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-blue-500"></div>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
              DeepSeek-V3
            </span>
          </div>
          <div className="space-y-2 font-mono text-[11px]">
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.equity}</span>
              <span className="font-semibold text-white">
                ${(accountData.chartData[accountData.chartData.length - 1]?.deepseek || botInitialBalances.deepseek).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.roi}</span>
              <span className={`font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                +0.00%
              </span>
            </div>
          </div>
        </div>

        {/* Doubao Card */}
        <div className="rounded border border-purple-500/20 bg-zinc-950/50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-purple-500"></div>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
              Doubao
            </span>
          </div>
          <div className="space-y-2 font-mono text-[11px]">
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.equity}</span>
              <span className="font-semibold text-white">
                ${(accountData.chartData[accountData.chartData.length - 1]?.doubao || botInitialBalances.doubao).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.roi}</span>
              <span className={`font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                +0.00%
              </span>
            </div>
          </div>
        </div>

        {/* Qwen Card */}
        <div className="rounded border border-orange-500/20 bg-zinc-950/50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-orange-500"></div>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
              Qwen
            </span>
          </div>
          <div className="space-y-2 font-mono text-[11px]">
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.equity}</span>
              <span className="font-semibold text-white">
                ${(accountData.chartData[accountData.chartData.length - 1]?.qwen || botInitialBalances.qwen).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.roi}</span>
              <span className={`font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                +0.00%
              </span>
            </div>
          </div>
        </div>

        {/* MACD Strategy Card */}
        <div className="rounded border border-emerald-500/20 bg-zinc-950/50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-400">
              MACD Strategy
            </span>
          </div>
          <div className="space-y-2 font-mono text-[11px]">
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.equity}</span>
              <span className="font-semibold text-white">
                ${(accountData.chartData[accountData.chartData.length - 1]?.macd || botInitialBalances.macd).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t.common.roi}</span>
              <span className={`font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                +0.00%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
